import {
  MutableRefObject,
  ReactNode,
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";

type Props = {
  tabs: { value: string; label: string }[];
  value: string;
  onClick: (value: string) => void;
  className?: string;
};

type TabsContextType = {
  value: string;
  onValueChange: (value: string) => void;
  activeTabBlockStyle: { width: number; left: number };
  refs: MutableRefObject<(HTMLElement | null)[]>;
};

const TabsContext = createContext<TabsContextType | null>(null);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("Tabs compound components must be used within RegionTabs");
  }
  return context;
};

export default function Tabs({ tabs, value, onClick, className }: Props) {
  const [activeTabBlockStyle, setActiveTabBlockStyle] = useState({ width: 0, left: 0 });
  const refs = useRef([]);

  // TODO: это много где используется, наде делать либо хук либо выносить в Табы как компоненту BordeTabs, FillTabs
  useLayoutEffect(() => {
    const index = tabs.findIndex((item) => item.value === value);

    if (refs.current[index]) {
      const activeTabElement = refs.current[index];
      const { offsetWidth, offsetLeft } = activeTabElement;

      setActiveTabBlockStyle({
        width: offsetWidth,
        left: offsetLeft,
      });
    }
  }, [value, tabs]);

  return (
    <div className={cn("relative h-[3.75rem] gap-5 flex items-stretch", className)}>
      {tabs.map((item, index) => (
        <button
          type="button"
          key={item.value}
          onClick={() => onClick(item.value)}
          className={clsx(
            "flex items-center text-12 font-extrabold uppercase hover:text-accent-solid transition-all",
            {
              "text-accent-solid": item.value === value,
            },
          )}
          ref={(el) => {
            refs.current[index] = el;
            return el;
          }}
        >
          {item.label}
        </button>
      ))}
      <div
        className="absolute h-full border-b border-accent-solid transition-all duration-300 z-0"
        style={{
          width: `${activeTabBlockStyle.width}px`,
          transform: `translateX(${activeTabBlockStyle.left}px)`,
        }}
      />
    </div>
  );
}

type RegionTabsProps = {
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  className?: string;
};

type TabsListProps = {
  children: ReactNode;
  className?: string;
};

type TabsTriggerProps = {
  value: string;
  children: ReactNode;
  className?: string;
};

type TabsIndicatorProps = {
  className?: string;
};

export function RegionTabs({ value, onValueChange, children, className }: RegionTabsProps) {
  const [activeTabBlockStyle, setActiveTabBlockStyle] = useState({ width: 0, left: 0 });
  const refs = useRef<(HTMLElement | null)[]>([]);

  useLayoutEffect(() => {
    const index = refs.current.findIndex((ref) => ref?.dataset.tabValue === value);
    if (refs.current[index]) {
      const activeTabElement = refs.current[index];
      const { offsetWidth, offsetLeft } = activeTabElement;

      setActiveTabBlockStyle({
        width: offsetWidth,
        left: offsetLeft,
      });
    }
  }, [value]);

  const contextValue = useMemo(
    () => ({
      value,
      onValueChange,
      activeTabBlockStyle,
      refs,
    }),
    [value, onValueChange, activeTabBlockStyle, refs],
  );

  return (
    <TabsContext.Provider value={contextValue}>
      <div className={cn("relative font-tt-norms-pro", className)}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsList({ children, className }: TabsListProps) {
  return (
    <div className={cn("relative h-[3.75rem] gap-5 flex items-stretch", className)}>{children}</div>
  );
}

export function TabsTrigger({ value, children, className }: TabsTriggerProps) {
  const { value: selectedValue, onValueChange, refs } = useTabsContext();
  const isSelected = selectedValue === value;

  return (
    <button
      type="button"
      onClick={() => onValueChange(value)}
      aria-selected={isSelected}
      className={clsx(
        "flex items-center text-12 font-extrabold uppercase hover:text-accent-solid transition-all",
        "aria-selected:text-accent-solid",
        className,
      )}
      ref={(el) => {
        const index = refs.current.findIndex((ref) => ref?.dataset.tabValue === value);
        if (index >= 0) {
          refs.current[index] = el;
        } else {
          refs.current.push(el);
        }
        if (el) {
          // eslint-disable-next-line no-param-reassign
          el.dataset.tabValue = value;
        }
      }}
    >
      {children}
    </button>
  );
}

export function TabsIndicator({ className }: TabsIndicatorProps) {
  const { activeTabBlockStyle } = useTabsContext();

  return (
    <div
      className={cn(
        "absolute bottom-0 h-px bg-accent-solid transition-all duration-300 z-0",
        className,
      )}
      style={{
        width: `${activeTabBlockStyle.width}px`,
        transform: `translateX(${activeTabBlockStyle.left}px)`,
      }}
    />
  );
}
